import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Spin } from 'antd';
import DomainInfo from '../../helpers/DomainInfo';
import {
  isFramed,
  parentOrigin,
  rememberEmbed,
  startPath
} from './embedMode';
import styles from './embed.module.css';

/*
  Embed anywhere, the pages that run before the normal app:
    /embed?key=...           inside the member's website (embed.js puts it in a frame)
    /embed/signin?key=...    the small first-party sign-in window the frame opens
    /embed/preview?key=...   "Preview it" from Account > Embed: the embed framed on CWU itself
  Server side: InfusionsoftIntegration services/api/embed/embedRoutes.js (how sign-in works is there).
*/

type View = { mode: string; courseId?: number };
const API = DomainInfo.baseUrl;
const query = () => new URLSearchParams(window.location.search);

const errorCode = (e: any) => e?.response?.data?.code;

/** A random value the frame gives the sign-in window, so it can collect the sign-in itself. */
const newPair = () => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const Card = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.wrap}>
    <div className={styles.card}>
      <div className={styles.brand}>CLUB WEALTH UNIVERSITY</div>
      {children}
    </div>
  </div>
);

/* ---------- /embed/signin: runs in the popup, on CWU itself ---------- */

const EmbedSignIn = () => {
  const [state, setState] = useState<'working' | 'done' | 'error' | 'noOpener'>('working');
  useEffect(() => {
    const key = query().get('key') || '';
    const pair = query().get('pair') || '';
    const back = `/embed/signin?key=${encodeURIComponent(key)}${pair ? `&pair=${encodeURIComponent(pair)}` : ''}`;
    const token = window.localStorage.getItem('CWAuthToken');
    if (!token) {
      // Sign in normally, then come straight back here (App.tsx enterCWU follows beforeLogin).
      window.localStorage.setItem('beforeLogin', back);
      window.location.href = '/login';
      return;
    }
    if (!window.opener && !pair) {
      setState('noOpener');
      return;
    }
    axios
      .post(`${API}/api/embed/handoff`, { key, pair }, { headers: { AuthToken: token } })
      .then((res) => {
        // Only CWU's own pages can receive this: the frame that opened us is on this same origin.
        // Google/Microsoft sign-in can cut window.opener; the frame then collects it by `pair` instead.
        if (window.opener) window.opener.postMessage({ type: 'cwu-embed-code', code: res.data.code }, window.location.origin);
        setState('done');
        setTimeout(() => window.close(), 300);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          window.localStorage.removeItem('CWAuthToken');
          window.localStorage.setItem('beforeLogin', back);
          window.location.href = '/login';
          return;
        }
        setState('error');
      });
  }, []);

  return (
    <Card>
      {state === 'working' && <Spin />}
      {state === 'done' && <p className={styles.text}>You are signed in. You can close this window.</p>}
      {state === 'noOpener' && (
        <p className={styles.text}>Open this from the Club Wealth University section of your team's website.</p>
      )}
      {state === 'error' && (
        <p className={styles.text}>This embed is not active right now. Ask your team leader to check their embed settings.</p>
      )}
    </Card>
  );
};

/* ---------- /embed/preview: the embed framed on CWU itself ---------- */

const EmbedPreview = () => {
  const key = query().get('key') || '';
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(800);
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return;
      if (e.data && e.data.type === 'cwu-embed-height' && e.data.height > 0) setHeight(Math.min(e.data.height, 20000));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  return (
    <div className={styles.previewPage}>
      <div className={styles.previewBar}>
        Preview: this is what your team sees on your website. It uses your own sign-in here.
      </div>
      <iframe
        ref={frame}
        title='Club Wealth University'
        src={`/embed?key=${encodeURIComponent(key)}`}
        className={styles.previewFrame}
        style={{ height }}
        allow='fullscreen; autoplay; encrypted-media; picture-in-picture; storage-access'
      />
    </div>
  );
};

/* ---------- /embed: inside the member's website ---------- */

type Phase = 'loading' | 'signin' | 'refused' | 'inactive' | 'working';

const EmbedFrame = () => {
  const key = query().get('key') || '';
  const partnerCode = query().get('pc');
  const origin = parentOrigin();
  // CWU framed by itself (the preview) shares storage with the normal website: never overwrite that login.
  const sameSite = !isFramed() || origin === window.location.origin;
  const [phase, setPhase] = useState<Phase>('loading');
  const [view, setView] = useState<View | null>(null);
  const [note, setNote] = useState('');
  const popup = useRef<Window | null>(null);
  const done = useRef(false);
  const poll = useRef<number | null>(null);

  const stopPolling = () => {
    if (poll.current) window.clearInterval(poll.current);
    poll.current = null;
  };
  useEffect(() => stopPolling, []);

  const enter = useCallback(
    async (v: View | null) => {
      const token = window.localStorage.getItem('CWAuthToken');
      // Logs the open for the owner, and doubles as "is this session still good".
      await axios.post(`${API}/api/embed/opened`, {}, { headers: { AuthToken: token } });
      window.location.replace(startPath(v));
    },
    []
  );

  const exchange = useCallback(
    async (code: string) => {
      if (done.current) return;
      done.current = true;
      stopPolling();
      setPhase('working');
      try {
        const res = await axios.post(`${API}/api/public/embed/exchange`, {
          key,
          code,
          parentOrigin: sameSite ? window.location.origin : origin
        });
        await finish(res.data);
      } catch (e) {
        done.current = false;
        failed(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, origin, sameSite, enter]
  );

  const finish = async (data: { token: string; view: View }) => {
    if (!data || typeof data.token !== 'string') throw new Error('no session in the answer');
    if (!sameSite) {
      window.localStorage.setItem('CWAuthToken', data.token);
      rememberEmbed(key, origin);
    }
    await enter(data.view);
  };

  const failed = (e: any) => {
    const c = errorCode(e);
    setNote(
      c === 'ORIGIN_REFUSED'
        ? 'This website is not on the list for this embed.'
        : c === 'NOT_ON_TEAM'
        ? 'Sign in with your own Club Wealth account.'
        : 'That sign-in did not go through. Try again.'
    );
    setPhase(c === 'ORIGIN_REFUSED' ? 'refused' : 'signin');
  };

  // While the sign-in window is open, ask the server every 2 seconds whether it finished (see /claim).
  const startPolling = (pair: string) => {
    stopPolling();
    const started = Date.now();
    poll.current = window.setInterval(async () => {
      if (done.current || Date.now() - started > 5 * 60 * 1000) return stopPolling();
      try {
        const res = await axios.post(`${API}/api/public/embed/claim`, {
          key,
          pair,
          parentOrigin: sameSite ? window.location.origin : origin
        });
        if (res.status !== 200 || done.current) return;
        done.current = true;
        stopPolling();
        setPhase('working');
        await finish(res.data);
      } catch (e) {
        if (e && (e as any).response && (e as any).response.status === 429) return;
        done.current = false;
        stopPolling();
        failed(e);
      }
    }, 2000);
  };

  // The popup sends back a one-time code. Only from CWU's own origin and only from the window we opened.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (!e.data || e.data.type !== 'cwu-embed-code' || typeof e.data.code !== 'string') return;
      if (popup.current && e.source !== popup.current) return;
      exchange(e.data.code);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [exchange]);

  useEffect(() => {
    let alive = true;
    (async () => {
      let cfg: { view: View; allowedHosts: string[] };
      try {
        cfg = (await axios.get(`${API}/api/public/embed/config`, { params: { key } })).data;
      } catch (e) {
        if (alive) setPhase('inactive');
        return;
      }
      // A web page instead of settings (CloudFront answers some errors with index.html) = not active.
      if (!cfg || typeof cfg !== 'object' || !Array.isArray(cfg.allowedHosts)) {
        if (alive) setPhase('inactive');
        return;
      }
      if (!alive) return;
      setView(cfg.view);
      // First check, in the browser: the website around us must be on the member's list.
      let host = '';
      try {
        host = origin ? new URL(origin).hostname : '';
      } catch (e) {
        host = '';
      }
      if (isFramed() && !cfg.allowedHosts.includes(host)) {
        setPhase('refused');
        return;
      }
      if (partnerCode) {
        exchange(partnerCode);
        return;
      }
      if (window.localStorage.getItem('CWAuthToken')) {
        try {
          await enter(cfg.view);
          return;
        } catch (e) {
          if (!sameSite) window.localStorage.removeItem('CWAuthToken');
        }
      }
      if (alive) setPhase('signin');
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async () => {
    setNote('');
    // Browsers that allow it (Storage Access API) can use the member's existing CWU login directly.
    try {
      const doc: any = document;
      if (!sameSite && doc.requestStorageAccess) {
        const handle = await doc.requestStorageAccess({ localStorage: true });
        const firstParty = handle && handle.localStorage && handle.localStorage.getItem('CWAuthToken');
        if (firstParty) {
          const res = await axios.post(`${API}/api/embed/handoff`, { key }, { headers: { AuthToken: firstParty } });
          await exchange(res.data.code);
          return;
        }
      }
    } catch (e) {
      // not granted or not supported: use the sign-in window
    }
    const pair = newPair();
    popup.current = window.open(
      `/embed/signin?key=${encodeURIComponent(key)}&pair=${pair}`,
      'cwu-embed-signin',
      'width=480,height=680'
    );
    if (!popup.current) {
      setNote('Your browser blocked the sign-in window. Allow pop-ups for this site and try again.');
      return;
    }
    startPolling(pair);
  };

  if (phase === 'loading' || phase === 'working') {
    return (
      <div className={styles.wrap}>
        <Spin />
      </div>
    );
  }
  if (phase === 'inactive') {
    return (
      <Card>
        <p className={styles.text}>This Club Wealth University embed is not active.</p>
      </Card>
    );
  }
  if (phase === 'refused') {
    return (
      <Card>
        <p className={styles.text}>
          Club Wealth University is not set up to show on this website. The team leader can add it on the
          Embed page in Club Wealth University.
        </p>
      </Card>
    );
  }
  return (
    <Card>
      <h2 className={styles.title}>
        {view && view.mode === 'coaching' ? 'Your coaching board lives here' : "Your team's training lives here"}
      </h2>
      <p className={styles.text}>
        Sign in once with your Club Wealth account. After that this page opens straight to it every time.
      </p>
      <Button type='primary' size='large' onClick={signIn}>
        Sign in to Club Wealth
      </Button>
      {note && <p className={styles.note}>{note}</p>}
      <p className={styles.small}>Opens a small sign-in window. No account yet? Ask your team leader for an invite.</p>
    </Card>
  );
};

const EmbedEntry = () => {
  const path = window.location.pathname;
  if (path.startsWith('/embed/signin')) return <EmbedSignIn />;
  if (path.startsWith('/embed/preview')) return <EmbedPreview />;
  return <EmbedFrame />;
};

export default EmbedEntry;
